import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import useSeo from '../hooks/useSeo';

// Eye with a tracking pupil. Follows the cursor unless forceLookX/Y is set.
function EyeBall({
  size = 18,
  pupilSize = 7,
  maxDistance = 5,
  eyeColor = '#fff',
  pupilColor = '#2D2D2D',
  isBlinking = false,
  forceLookX,
  forceLookY,
  mouseX,
  mouseY,
}) {
  const eyeRef = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (forceLookX !== undefined && forceLookY !== undefined) {
      setPos({ x: forceLookX, y: forceLookY });
      return;
    }
    const el = eyeRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = mouseX - cx;
    const dy = mouseY - cy;
    const distance = Math.min(Math.sqrt(dx * dx + dy * dy), maxDistance);
    const angle = Math.atan2(dy, dx);
    setPos({ x: Math.cos(angle) * distance, y: Math.sin(angle) * distance });
  }, [mouseX, mouseY, maxDistance, forceLookX, forceLookY]);

  return (
    <div
      ref={eyeRef}
      style={{
        width: size,
        height: isBlinking ? 2 : size,
        background: eyeColor,
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'height 120ms ease',
      }}
    >
      {!isBlinking && (
        <div
          style={{
            width: pupilSize,
            height: pupilSize,
            background: pupilColor,
            borderRadius: '50%',
            transform: `translate(${pos.x}px, ${pos.y}px)`,
            transition: 'transform 0.1s ease-out',
          }}
        />
      )}
    </div>
  );
}

// Pupil-only (no sclera) used on the orange + yellow characters.
function Pupil({ size = 12, maxDistance = 5, pupilColor = '#2D2D2D', forceLookX, forceLookY, mouseX, mouseY }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (forceLookX !== undefined && forceLookY !== undefined) {
      setPos({ x: forceLookX, y: forceLookY });
      return;
    }
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = mouseX - cx;
    const dy = mouseY - cy;
    const distance = Math.min(Math.sqrt(dx * dx + dy * dy), maxDistance);
    const angle = Math.atan2(dy, dx);
    setPos({ x: Math.cos(angle) * distance, y: Math.sin(angle) * distance });
  }, [mouseX, mouseY, maxDistance, forceLookX, forceLookY]);

  return (
    <div
      ref={ref}
      style={{
        width: size,
        height: size,
        background: pupilColor,
        borderRadius: '50%',
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        transition: 'transform 0.1s ease-out',
      }}
    />
  );
}

export default function ContactUs() {
  useSeo({
    title: 'Contact Us',
    description: "Get in touch with It's a Simcha — questions about custom Jewish invitation templates, custom designs, refunds, or technical support.",
    path: '/contact-us',
  });
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [purpleBlink, setPurpleBlink] = useState(false);
  const [blackBlink, setBlackBlink] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [lookAtEachOther, setLookAtEachOther] = useState(false);

  const purpleRef = useRef(null);
  const blackRef = useRef(null);
  const yellowRef = useRef(null);
  const orangeRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => { setMouseX(e.clientX); setMouseY(e.clientY); };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  // Independent random blinks for the two characters with eyes.
  useEffect(() => {
    let t;
    const loop = () => {
      t = setTimeout(() => {
        setPurpleBlink(true);
        setTimeout(() => { setPurpleBlink(false); loop(); }, 150);
      }, 3000 + Math.random() * 4000);
    };
    loop();
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    let t;
    const loop = () => {
      t = setTimeout(() => {
        setBlackBlink(true);
        setTimeout(() => { setBlackBlink(false); loop(); }, 150);
      }, 3000 + Math.random() * 4000);
    };
    loop();
    return () => clearTimeout(t);
  }, []);

  // When the user starts typing, the two eyed characters glance at each other
  // briefly, then resume tracking the mouse.
  useEffect(() => {
    if (isTyping) {
      setLookAtEachOther(true);
      const t = setTimeout(() => setLookAtEachOther(false), 800);
      return () => clearTimeout(t);
    }
    setLookAtEachOther(false);
  }, [isTyping]);

  function calcBody(ref) {
    if (!ref.current) return { faceX: 0, faceY: 0, bodySkew: 0 };
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 3;
    const dx = mouseX - cx;
    const dy = mouseY - cy;
    return {
      faceX: Math.max(-15, Math.min(15, dx / 20)),
      faceY: Math.max(-10, Math.min(10, dy / 30)),
      bodySkew: Math.max(-6, Math.min(6, -dx / 120)),
    };
  }
  const purple = calcBody(purpleRef);
  const black = calcBody(blackRef);
  const yellow = calcBody(yellowRef);
  const orange = calcBody(orangeRef);

  function set(k) { return (e) => setForm((f) => ({ ...f, [k]: e.target.value })); }

  async function onSubmit(e) {
    e.preventDefault();
    setSending(true);
    setErr('');
    try {
      await api.post('/mail/SendReachoutMail', form);
      setSent(true);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      const errorMsg = err?.response?.data?.body || err?.message || 'Something went wrong. Please email us directly at system@itsasimcha.net.';
      setErr(errorMsg);
      console.error('Contact form error:', err);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="contact-split">
      <style>{`
        .contact-split {
          display: grid;
          grid-template-columns: 1fr 1fr;
          min-height: calc(100vh - 100px);
          margin: 18px;
          border-radius: 28px;
          overflow: hidden;
          background: #fff;
          box-shadow: 0 4px 30px rgba(30,60,90,.08);
        }
        @media (max-width: 960px) {
          .contact-split { grid-template-columns: 1fr; }
          .contact-split .characters-panel { display: none; }
        }
        .characters-panel {
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 3rem;
          background: linear-gradient(135deg, #29B4F1 0%, #1990C7 60%, #0e6f9f 100%);
          color: #fff;
          overflow: hidden;
        }
        .characters-panel .brand-line {
          position: relative;
          z-index: 2;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          font-size: 1.05rem;
          letter-spacing: 0.02em;
        }
        .characters-panel .brand-chip {
          width: 32px; height: 32px;
          border-radius: 8px;
          background: rgba(255,255,255,.15);
          display: flex; align-items: center; justify-content: center;
          backdrop-filter: blur(6px);
          font-size: 18px;
        }
        .characters-panel .characters-stage {
          position: relative;
          z-index: 2;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          height: 440px;
        }
        .characters-panel .decor {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          z-index: 1;
        }
        .characters-panel .decor-1 { top: 20%; right: 20%; width: 16rem; height: 16rem; background: rgba(255,255,255,.10); filter: blur(60px); }
        .characters-panel .decor-2 { bottom: 25%; left: 20%; width: 24rem; height: 24rem; background: rgba(255,255,255,.06); filter: blur(80px); }
        .characters-panel .footer-links {
          position: relative; z-index: 2;
          display: flex; gap: 2rem; font-size: 0.85rem;
          color: rgba(255,255,255,.7);
        }
        .characters-panel .footer-links a { color: inherit; }
        .characters-panel .footer-links a:hover { color: #fff; }

        .form-panel {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          background: #ffffff;
        }
        .form-panel .inner { width: 100%; max-width: 440px; }
        .form-panel h1 {
          font-size: 1.9rem;
          margin: 0 0 0.4rem;
          color: #1a2533;
          letter-spacing: -0.01em;
        }
        .form-panel .lead {
          color: #5b6b7c;
          font-size: 0.95rem;
          margin: 0 0 2rem;
        }
        .form-panel .field { margin-bottom: 1rem; }
        .form-panel label {
          display: block;
          font-size: 0.8rem;
          font-weight: 600;
          color: #415364;
          margin-bottom: 0.35rem;
        }
        .form-panel input,
        .form-panel textarea {
          width: 100%;
          padding: 0.75rem 0.9rem;
          border: 1px solid #dfecf5;
          border-radius: 10px;
          background: #fafdff;
          color: #1a2533;
          font: inherit;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .form-panel textarea { min-height: 120px; resize: vertical; }
        .form-panel input:focus,
        .form-panel textarea:focus {
          border-color: #29B4F1;
          box-shadow: 0 0 0 3px rgba(41,180,241,.15);
        }
        .form-panel .submit-btn {
          width: 100%;
          padding: 0.85rem;
          border: none;
          border-radius: 10px;
          background: #29B4F1;
          color: #fff;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: background 0.15s, transform 0.15s;
          margin-top: 0.5rem;
        }
        .form-panel .submit-btn:hover:not(:disabled) { background: #1990C7; transform: translateY(-1px); }
        .form-panel .submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Animated send button (blue theme) */
        .send-btn {
          --primary: #ffffff;
          --neutral-1: #29B4F1;
          --neutral-2: #1990C7;
          --radius: 14px;
          cursor: pointer;
          border-radius: var(--radius);
          text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
          border: none;
          color: #ffffff;
          box-shadow: 0 0.5px 0.5px 1px rgba(255, 255, 255, 0.2),
            0 10px 20px rgba(14, 111, 159, 0.35),
            0 4px 5px 0px rgba(0, 0, 0, 0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          transition: all 0.3s ease;
          width: 100%;
          padding: 20px;
          height: 68px;
          font-family: Poppins, Montserrat, sans-serif;
          font-style: normal;
          font-size: 18px;
          font-weight: 600;
          margin-top: 0.5rem;
        }
        .send-btn:hover:not(:disabled) {
          transform: scale(1.02);
          box-shadow: 0 0 1px 2px rgba(255, 255, 255, 0.3),
            0 15px 30px rgba(14, 111, 159, 0.45),
            0 10px 3px -3px rgba(0, 0, 0, 0.06);
        }
        .send-btn:active:not(:disabled) {
          transform: scale(1);
          box-shadow: 0 0 1px 2px rgba(255, 255, 255, 0.3),
            0 10px 3px -3px rgba(0, 0, 0, 0.25);
        }
        .send-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .send-btn:after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: var(--radius);
          border: 2.5px solid transparent;
          background: linear-gradient(var(--neutral-1), var(--neutral-2)) padding-box,
            linear-gradient(to bottom, rgba(255, 255, 255, 0.35), rgba(0, 0, 0, 0.35)) border-box;
          z-index: 0;
          transition: all 0.4s ease;
        }
        .send-btn:hover::after {
          transform: scale(1.05, 1.1);
          box-shadow: inset 0 -1px 3px 0 rgba(255, 255, 255, 1);
        }
        .send-btn::before {
          content: "";
          inset: 7px 6px 6px 6px;
          position: absolute;
          background: linear-gradient(to top, var(--neutral-1), var(--neutral-2));
          border-radius: 30px;
          filter: blur(0.5px);
          z-index: 2;
        }
        .send-btn .state { padding-left: 29px; z-index: 3; display: flex; position: relative; }
        .send-btn .state p { display: flex; align-items: center; justify-content: center; margin: 0; }
        .send-btn .state .icon {
          position: absolute;
          left: 0; top: 0; bottom: 0;
          margin: auto;
          transform: scale(1.25);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .send-btn .state .icon svg { overflow: visible; }

        .send-btn .outline {
          position: absolute;
          border-radius: inherit;
          overflow: hidden;
          z-index: 1;
          opacity: 0;
          transition: opacity 0.4s ease;
          inset: -2px -3.5px;
        }
        .send-btn .outline::before {
          content: "";
          position: absolute;
          inset: -100%;
          background: conic-gradient(from 180deg, transparent 60%, white 80%, transparent 100%);
          animation: send-btn-spin 2s linear infinite;
          animation-play-state: paused;
        }
        @keyframes send-btn-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .send-btn:hover .outline { opacity: 1; }
        .send-btn:hover .outline::before { animation-play-state: running; }

        .send-btn .state p span {
          display: block;
          opacity: 0;
          animation: send-btn-slideDown 0.8s ease forwards calc(var(--i) * 0.03s);
        }
        .send-btn:hover p span {
          opacity: 1;
          animation: send-btn-wave 0.5s ease forwards calc(var(--i) * 0.02s);
        }
        .send-btn.is-sending p span,
        .send-btn:focus p span {
          opacity: 1;
          animation: send-btn-disapear 0.6s ease forwards calc(var(--i) * 0.03s);
        }
        @keyframes send-btn-wave {
          30% { opacity: 1; transform: translateY(4px) translateX(0) rotate(0); }
          50% { opacity: 1; transform: translateY(-3px) translateX(0) rotate(0); color: var(--primary); }
          100% { opacity: 1; transform: translateY(0) translateX(0) rotate(0); }
        }
        @keyframes send-btn-slideDown {
          0% { opacity: 0; transform: translateY(-20px) translateX(5px) rotate(-90deg); color: var(--primary); filter: blur(5px); }
          30% { opacity: 1; transform: translateY(4px) translateX(0) rotate(0); filter: blur(0); }
          50% { opacity: 1; transform: translateY(-3px) translateX(0) rotate(0); }
          100% { opacity: 1; transform: translateY(0) translateX(0) rotate(0); }
        }
        @keyframes send-btn-disapear {
          from { opacity: 1; }
          to { opacity: 0; transform: translateX(5px) translateY(20px); color: var(--primary); filter: blur(5px); }
        }

        .send-btn .state--default .icon svg { animation: send-btn-land 0.6s ease forwards; }
        .send-btn:hover .state--default .icon { transform: rotate(45deg) scale(1.25); }
        .send-btn.is-sending .state--default svg,
        .send-btn:focus .state--default svg { animation: send-btn-takeOff 0.8s linear forwards; }
        .send-btn.is-sending .state--default .icon,
        .send-btn:focus .state--default .icon { transform: rotate(0) scale(1.25); }
        @keyframes send-btn-takeOff {
          0% { opacity: 1; }
          60% { opacity: 1; transform: translateX(70px) rotate(45deg) scale(2); }
          100% { opacity: 0; transform: translateX(160px) rotate(45deg) scale(0); }
        }
        @keyframes send-btn-land {
          0% { transform: translateX(-60px) translateY(30px) rotate(-50deg) scale(2); opacity: 0; filter: blur(3px); }
          100% { transform: translateX(0) translateY(0) rotate(0); opacity: 1; filter: blur(0); }
        }

        .send-btn .state--default .icon:before {
          content: "";
          position: absolute;
          top: 50%;
          height: 2px;
          width: 0;
          left: -5px;
          background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.7));
        }
        .send-btn.is-sending .state--default .icon:before,
        .send-btn:focus .state--default .icon:before { animation: send-btn-contrail 0.8s linear forwards; }
        @keyframes send-btn-contrail {
          0% { width: 0; opacity: 1; }
          8% { width: 15px; }
          60% { opacity: 0.7; width: 80px; }
          100% { opacity: 0; width: 160px; }
        }

        .send-btn .state--default span:nth-child(4) { margin-right: 5px; }
        .send-btn .state--sent { display: none; }
        .send-btn .state--sent svg { transform: scale(1.25); margin-right: 8px; }
        .send-btn.is-sending .state--default,
        .send-btn:focus .state--default { position: absolute; }
        .send-btn.is-sending .state--sent,
        .send-btn:focus .state--sent { display: flex; }
        .send-btn.is-sending .state--sent span,
        .send-btn:focus .state--sent span {
          opacity: 0;
          animation: send-btn-slideDown 0.8s ease forwards calc(var(--i) * 0.2s);
        }
        .send-btn.is-sending .state--sent .icon svg,
        .send-btn:focus .state--sent .icon svg {
          opacity: 0;
          animation: send-btn-appear 1.2s ease forwards 0.8s;
        }
        @keyframes send-btn-appear {
          0% { opacity: 0; transform: scale(4) rotate(-40deg); color: var(--primary); filter: blur(4px); }
          30% { opacity: 1; transform: scale(0.6); filter: blur(1px); }
          50% { opacity: 1; transform: scale(1.2); filter: blur(0); }
          100% { opacity: 1; transform: scale(1); }
        }

        .form-panel .err { color: #dc3545; font-size: 0.85rem; margin-top: 0.5rem; }
        .form-panel .foot-line { text-align: center; font-size: 0.85rem; color: #5b6b7c; margin-top: 1.5rem; }
        .form-panel .foot-line a { color: #29B4F1; font-weight: 600; }
      `}</style>

      <div className="characters-panel">
        <div className="characters-stage">
          <div style={{ position: 'relative', width: 550, height: 400 }}>
            {/* Purple tall rectangle */}
            <div
              ref={purpleRef}
              style={{
                position: 'absolute',
                bottom: 0,
                left: 70,
                width: 180,
                height: isTyping ? 440 : 400,
                background: '#6C3FF5',
                borderRadius: '10px 10px 0 0',
                zIndex: 1,
                transformOrigin: 'bottom center',
                transform: lookAtEachOther || isTyping
                  ? `skewX(${(purple.bodySkew || 0) - 12}deg) translateX(40px)`
                  : `skewX(${purple.bodySkew || 0}deg)`,
                transition: 'transform 700ms cubic-bezier(0.16, 1, 0.3, 1), height 700ms cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  display: 'flex',
                  gap: 32,
                  left: lookAtEachOther ? 55 : 45 + purple.faceX,
                  top: lookAtEachOther ? 65 : 40 + purple.faceY,
                  transition: 'left 700ms cubic-bezier(0.16, 1, 0.3, 1), top 700ms cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                <EyeBall size={18} pupilSize={7} maxDistance={5} isBlinking={purpleBlink} mouseX={mouseX} mouseY={mouseY}
                  forceLookX={lookAtEachOther ? 3 : undefined} forceLookY={lookAtEachOther ? 4 : undefined} />
                <EyeBall size={18} pupilSize={7} maxDistance={5} isBlinking={purpleBlink} mouseX={mouseX} mouseY={mouseY}
                  forceLookX={lookAtEachOther ? 3 : undefined} forceLookY={lookAtEachOther ? 4 : undefined} />
              </div>
            </div>

            {/* Black tall rectangle */}
            <div
              ref={blackRef}
              style={{
                position: 'absolute',
                bottom: 0,
                left: 240,
                width: 120,
                height: 310,
                background: '#2D2D2D',
                borderRadius: '8px 8px 0 0',
                zIndex: 2,
                transformOrigin: 'bottom center',
                transform: lookAtEachOther
                  ? `skewX(${(black.bodySkew || 0) * 1.5 + 10}deg) translateX(20px)`
                  : isTyping
                    ? `skewX(${(black.bodySkew || 0) * 1.5}deg)`
                    : `skewX(${black.bodySkew || 0}deg)`,
                transition: 'transform 700ms cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  display: 'flex',
                  gap: 24,
                  left: lookAtEachOther ? 32 : 26 + black.faceX,
                  top: lookAtEachOther ? 12 : 32 + black.faceY,
                  transition: 'left 700ms cubic-bezier(0.16, 1, 0.3, 1), top 700ms cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                <EyeBall size={16} pupilSize={6} maxDistance={4} isBlinking={blackBlink} mouseX={mouseX} mouseY={mouseY}
                  forceLookX={lookAtEachOther ? 0 : undefined} forceLookY={lookAtEachOther ? -4 : undefined} />
                <EyeBall size={16} pupilSize={6} maxDistance={4} isBlinking={blackBlink} mouseX={mouseX} mouseY={mouseY}
                  forceLookX={lookAtEachOther ? 0 : undefined} forceLookY={lookAtEachOther ? -4 : undefined} />
              </div>
            </div>

            {/* Orange semi-circle (front left) */}
            <div
              ref={orangeRef}
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: 240,
                height: 200,
                background: '#FF9B6B',
                borderRadius: '120px 120px 0 0',
                zIndex: 3,
                transformOrigin: 'bottom center',
                transform: `skewX(${orange.bodySkew || 0}deg)`,
                transition: 'transform 700ms cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  display: 'flex',
                  gap: 32,
                  left: 82 + (orange.faceX || 0),
                  top: 90 + (orange.faceY || 0),
                  transition: 'left 200ms ease-out, top 200ms ease-out',
                }}
              >
                <Pupil size={12} maxDistance={5} mouseX={mouseX} mouseY={mouseY} />
                <Pupil size={12} maxDistance={5} mouseX={mouseX} mouseY={mouseY} />
              </div>
            </div>

            {/* Yellow rounded-top rectangle (front right) */}
            <div
              ref={yellowRef}
              style={{
                position: 'absolute',
                bottom: 0,
                left: 310,
                width: 140,
                height: 230,
                background: '#E8D754',
                borderRadius: '70px 70px 0 0',
                zIndex: 4,
                transformOrigin: 'bottom center',
                transform: `skewX(${yellow.bodySkew || 0}deg)`,
                transition: 'transform 700ms cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  display: 'flex',
                  gap: 24,
                  left: 52 + (yellow.faceX || 0),
                  top: 40 + (yellow.faceY || 0),
                  transition: 'left 200ms ease-out, top 200ms ease-out',
                }}
              >
                <Pupil size={12} maxDistance={5} mouseX={mouseX} mouseY={mouseY} />
                <Pupil size={12} maxDistance={5} mouseX={mouseX} mouseY={mouseY} />
              </div>
              <div
                style={{
                  position: 'absolute',
                  width: 80,
                  height: 4,
                  background: '#2D2D2D',
                  borderRadius: 2,
                  left: 40 + (yellow.faceX || 0),
                  top: 88 + (yellow.faceY || 0),
                  transition: 'left 200ms ease-out, top 200ms ease-out',
                }}
              />
            </div>
          </div>
        </div>

        <div className="footer-links">
          <a href="/terms-conditions">Terms</a>
          <a href="mailto:system@itsasimcha.net">Email us</a>
        </div>

        <div className="decor decor-1" />
        <div className="decor decor-2" />
      </div>

      <div className="form-panel">
        <div className="inner">
          <h1>Let's talk.</h1>
          <p className="lead">Questions, custom requests, or just want to say hi? We'd love to hear from you.</p>

          <form onSubmit={onSubmit}>
              <div className="field">
                <label htmlFor="name">Your name</label>
                <input
                  id="name" type="text" required value={form.name} onChange={set('name')}
                  onFocus={() => setIsTyping(true)} onBlur={() => setIsTyping(false)}
                  placeholder="Joel"
                  disabled={sent}
                />
              </div>
              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  id="email" type="email" required value={form.email} onChange={set('email')}
                  onFocus={() => setIsTyping(true)} onBlur={() => setIsTyping(false)}
                  placeholder="joel@gmail.com"
                  disabled={sent}
                />
              </div>
              <div className="field">
                <label htmlFor="phone">Phone (optional)</label>
                <input
                  id="phone" type="tel" value={form.phone} onChange={set('phone')}
                  onFocus={() => setIsTyping(true)} onBlur={() => setIsTyping(false)}
                  placeholder="(555) 555-5555"
                  disabled={sent}
                />
              </div>
              <div className="field">
                <label htmlFor="message">How can we help?</label>
                <textarea
                  id="message" required value={form.message} onChange={set('message')}
                  onFocus={() => setIsTyping(true)} onBlur={() => setIsTyping(false)}
                  placeholder="Tell us about your simcha…"
                  disabled={sent}
                />
              </div>
              {err && <p className="err">{err}</p>}
              <button
                type="submit"
                className={`send-btn${sending || sent ? ' is-sending' : ''}`}
                disabled={sending || sent}
                aria-label={sent ? 'Sent' : 'Send Message'}
              >
                <div className="outline" />
                <div className="state state--default">
                  <div className="icon">
                    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g style={{ filter: 'url(#send-btn-shadow)' }}>
                        <path d="M14.2199 21.63C13.0399 21.63 11.3699 20.8 10.0499 16.83L9.32988 14.67L7.16988 13.95C3.20988 12.63 2.37988 10.96 2.37988 9.78001C2.37988 8.61001 3.20988 6.93001 7.16988 5.60001L15.6599 2.77001C17.7799 2.06001 19.5499 2.27001 20.6399 3.35001C21.7299 4.43001 21.9399 6.21001 21.2299 8.33001L18.3999 16.82C17.0699 20.8 15.3999 21.63 14.2199 21.63ZM7.63988 7.03001C4.85988 7.96001 3.86988 9.06001 3.86988 9.78001C3.86988 10.5 4.85988 11.6 7.63988 12.52L10.1599 13.36C10.3799 13.43 10.5599 13.61 10.6299 13.83L11.4699 16.35C12.3899 19.13 13.4999 20.12 14.2199 20.12C14.9399 20.12 16.0399 19.13 16.9699 16.35L19.7999 7.86001C20.3099 6.32001 20.2199 5.06001 19.5699 4.41001C18.9199 3.76001 17.6599 3.68001 16.1299 4.19001L7.63988 7.03001Z" fill="currentColor" />
                        <path d="M10.11 14.4C9.92005 14.4 9.73005 14.33 9.58005 14.18C9.29005 13.89 9.29005 13.41 9.58005 13.12L13.16 9.53C13.45 9.24 13.93 9.24 14.22 9.53C14.51 9.82 14.51 10.3 14.22 10.59L10.64 14.18C10.5 14.33 10.3 14.4 10.11 14.4Z" fill="currentColor" />
                      </g>
                      <defs>
                        <filter id="send-btn-shadow">
                          <feDropShadow dx={0} dy={1} stdDeviation="0.6" floodOpacity="0.5" />
                        </filter>
                      </defs>
                    </svg>
                  </div>
                  <p>
                    <span style={{ '--i': 0 }}>S</span>
                    <span style={{ '--i': 1 }}>e</span>
                    <span style={{ '--i': 2 }}>n</span>
                    <span style={{ '--i': 3 }}>d</span>
                    <span style={{ '--i': 4 }}>M</span>
                    <span style={{ '--i': 5 }}>e</span>
                    <span style={{ '--i': 6 }}>s</span>
                    <span style={{ '--i': 7 }}>s</span>
                    <span style={{ '--i': 8 }}>a</span>
                    <span style={{ '--i': 9 }}>g</span>
                    <span style={{ '--i': 10 }}>e</span>
                  </p>
                </div>
                <div className="state state--sent">
                  <div className="icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="1em" width="1em" strokeWidth="0.5px" stroke="black">
                      <g style={{ filter: 'url(#send-btn-shadow-2)' }}>
                        <path fill="currentColor" d="M12 22.75C6.07 22.75 1.25 17.93 1.25 12C1.25 6.07 6.07 1.25 12 1.25C17.93 1.25 22.75 6.07 22.75 12C22.75 17.93 17.93 22.75 12 22.75ZM12 2.75C6.9 2.75 2.75 6.9 2.75 12C2.75 17.1 6.9 21.25 12 21.25C17.1 21.25 21.25 17.1 21.25 12C21.25 6.9 17.1 2.75 12 2.75Z" />
                        <path fill="currentColor" d="M10.5795 15.5801C10.3795 15.5801 10.1895 15.5001 10.0495 15.3601L7.21945 12.5301C6.92945 12.2401 6.92945 11.7601 7.21945 11.4701C7.50945 11.1801 7.98945 11.1801 8.27945 11.4701L10.5795 13.7701L15.7195 8.6301C16.0095 8.3401 16.4895 8.3401 16.7795 8.6301C17.0695 8.9201 17.0695 9.4001 16.7795 9.6901L11.1095 15.3601C10.9695 15.5001 10.7795 15.5801 10.5795 15.5801Z" />
                      </g>
                      <defs>
                        <filter id="send-btn-shadow-2">
                          <feDropShadow dx={0} dy={1} stdDeviation="0.6" floodOpacity="0.5" />
                        </filter>
                      </defs>
                    </svg>
                  </div>
                  <p>
                    <span style={{ '--i': 5 }}>S</span>
                    <span style={{ '--i': 6 }}>e</span>
                    <span style={{ '--i': 7 }}>n</span>
                    <span style={{ '--i': 8 }}>t</span>
                  </p>
                </div>
              </button>
              {sent && (
                <p style={{ color: '#198754', textAlign: 'center', margin: '14px 0 0' }}>
                  Thanks! Your message is on its way — we'll reply shortly.{' '}
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    style={{ background: 'none', border: 'none', color: '#29B4F1', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                  >
                    Send another
                  </button>
                </p>
              )}
              <p className="foot-line">
                Or email us at <a href="mailto:system@itsasimcha.net">system@itsasimcha.net</a>
              </p>
            </form>
        </div>
      </div>
    </div>
  );
}
